import React, { useCallback, useEffect, useState } from 'react';
import TimeAgo from 'react-time-ago';
import {
  ApiError,
  CloseOutcome,
  CustomerRequest,
  CustomerRequestReason,
  CustomerRequestSource,
  CustomerRequestStatus,
  closeCustomerRequest,
  listCustomerRequests,
} from '../../api/customerRequests';
import AskCustomerDialog, { CaseDetailValues } from './AskCustomerDialog';
import { DOCUMENT_REASONS, detailLabel, reasonLabel } from './customerRequestCopy';
import '../FinancasOfficePicker/FinancasOfficePicker.css';
import './CustomerRequests.css';

/**
 * Requests to the customer on this case: what the lawyer asked for, where the
 * customer is with it, and what they sent back. Loads itself, like the
 * Finanças emails above it.
 *
 * Asking is behind `features.customer_requests` from /auth/me. With the flag
 * off the panel still lists requests a case already has, and stays out of the
 * way entirely when there are none.
 */

interface Props {
  caseId: string;
  user: { getIdToken: () => Promise<string> } | null;
  /** features.customer_requests: shows "Ask the customer". */
  canAsk: boolean;
  lawyerStatus?: string | null;
  /** applicant_type and type of the case: some reasons only fit a child's case or an ID card. */
  applicantType?: string | null;
  documentType?: string | null;
  caseValues: CaseDetailValues;
  /** Re-read the case: creating, accepting or cancelling moves lawyer_status. */
  onCaseChanged: () => void;
  /** Opens a client document by file name in a new tab (the signed-url route). */
  onViewDocument: (fileName: string) => void;
}

const STATUS_PILL: Record<CustomerRequestStatus, { label: string; tone: string }> = {
  open: { label: 'Open · waiting', tone: 'pending' },
  answered: { label: 'Answered · needs your look', tone: 'answered' },
  accepted: { label: 'Accepted', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value === 'object') {
    const seconds = value.seconds ?? value._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const When: React.FC<{ value: any }> = ({ value }) => {
  const date = toDate(value);
  return date ? <TimeAgo date={date} /> : null;
};

const newestFirst = (requests: CustomerRequest[]): CustomerRequest[] =>
  [...requests].sort(
    (a, b) => (toDate(b.created_at)?.getTime() ?? 0) - (toDate(a.created_at)?.getTime() ?? 0)
  );

const CheckLine: React.FC<{ request: CustomerRequest }> = ({ request }) => {
  const check = request.check;
  const lines: React.ReactNode[] = [];
  if (check?.status === 'checking') {
    lines.push(<span key="check" className="creq-check creq-check--checking">Checking</span>);
  } else if (check?.status === 'accepted') {
    lines.push(<span key="check" className="creq-check creq-check--ok">Check passed</span>);
  } else if (check?.status === 'not_accepted') {
    lines.push(
      <span key="check" className="creq-check creq-check--failed">
        Check failed{check.reason ? `: ${check.reason}` : ''}
      </span>
    );
  } else if (check?.status === 'unsure') {
    lines.push(
      <span key="check" className="creq-check creq-check--unsure">
        Check unsure{check.reason ? `: ${check.reason}` : ''}
      </span>
    );
  }
  if (request.sent_anyway) {
    lines.push(
      <span key="anyway" className="creq-check creq-check--failed">Sent anyway by the customer</span>
    );
  }
  if (lines.length === 0) return null;
  return <div className="creq-checks" data-testid="creq-check">{lines}</div>;
};

const ResponseBlock: React.FC<{
  request: CustomerRequest;
  onViewDocument: (fileName: string) => void;
}> = ({ request, onViewDocument }) => {
  const response = request.response;
  if (!response) return null;

  const fileRow = response.file_name ? (
    <div className="creq-file">
      <span className="creq-file-name">{response.file_name}</span>
      <button type="button" className="view-btn" onClick={() => onViewDocument(response.file_name as string)}>
        View
      </button>
    </div>
  ) : null;

  let body: React.ReactNode = null;
  if (DOCUMENT_REASONS.has(request.reason)) {
    body = fileRow;
  } else if (request.reason === 'sign_again') {
    body = (
      <p className="creq-answer">
        Signed {response.signed_at ? <When value={response.signed_at} /> : ''}
      </p>
    );
  } else if (request.reason === 'details') {
    const changed = response.changed ?? [];
    const values = response.values ?? {};
    const previous = response.previous ?? {};
    const fields = Object.keys(values);
    body = (
      <>
        <p className="creq-answer">
          {changed.length > 0
            ? `Changed: ${changed.map(detailLabel).join(', ')}`
            : response.confirmed
              ? 'Confirmed as shown'
              : 'No changes'}
        </p>
        {fields.length > 0 && (
          <table className="creq-details">
            <tbody>
              {fields.map(field => (
                <tr key={field} className={changed.includes(field) ? 'creq-details-row--changed' : undefined}>
                  <th scope="row">{detailLabel(field)}</th>
                  <td>
                    {changed.includes(field) && field in previous && (
                      <>
                        <s className="creq-details-old">{previous[field] || '(empty)'}</s>{' → '}
                      </>
                    )}
                    {values[field] || '(empty)'}
                  </td>
                  <td className="creq-details-flag">{changed.includes(field) ? 'changed' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  } else if (request.reason === 'financas_letter') {
    if (typeof response.open_letter === 'boolean') {
      body = (
        <p className="creq-answer">{response.open_letter ? 'Yes, open it' : 'No, keep it closed'}</p>
      );
    }
  } else if (request.reason === 'question') {
    body = (
      <>
        {response.choice && <p className="creq-answer creq-answer--choice">{response.choice}</p>}
        {response.text && <p className="creq-answer creq-note">{response.text}</p>}
        {fileRow}
      </>
    );
  }

  if (!body) return null;
  return (
    <div className="creq-response" data-testid="creq-response">
      <span className="creq-response-title">
        The customer answered{request.answered_at ? ' ' : ''}
        {request.answered_at && <When value={request.answered_at} />}
      </span>
      {body}
    </div>
  );
};

const CustomerRequests: React.FC<Props> = ({
  caseId,
  user,
  canAsk,
  lawyerStatus,
  applicantType,
  documentType,
  caseValues,
  onCaseChanged,
  onViewDocument,
}) => {
  const [requests, setRequests] = useState<CustomerRequest[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { reason: CustomerRequestReason | null; source: CustomerRequestSource | null } | null
  >(null);

  const load = useCallback(async () => {
    if (!user || !caseId) return;
    const token = await user.getIdToken();
    const list = await listCustomerRequests(token, caseId);
    return newestFirst(list);
  }, [caseId, user]);

  useEffect(() => {
    // Same guard as the Finanças emails: a slow answer for the case we just
    // left must not land under this one.
    let cancelled = false;
    setRequests(null);
    setLoadError('');
    setActionError('');
    load()
      .then(list => {
        if (!cancelled && list) setRequests(list);
      })
      .catch(err => {
        if (cancelled) return;
        // 404: a lawyer backend without the endpoint yet. Nothing to report.
        if (err instanceof ApiError && err.status === 404) {
          setRequests([]);
          return;
        }
        console.error('Error fetching customer requests:', err);
        setLoadError('Could not load the requests to the customer.');
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const refresh = async () => {
    try {
      const list = await load();
      if (list) setRequests(list);
      setLoadError('');
    } catch (err) {
      console.error('Error fetching customer requests:', err);
      setLoadError('Could not load the requests to the customer.');
    }
  };

  const handleCreated = () => {
    setDialog(null);
    refresh();
    onCaseChanged();
  };

  const handleClose = async (request: CustomerRequest, outcome: CloseOutcome) => {
    if (!user) return;
    if (outcome === 'cancelled' && !window.confirm('Cancel this request? The customer can no longer answer it.')) {
      return;
    }
    setBusyId(request.id);
    setActionError('');
    try {
      const token = await user.getIdToken();
      await closeCustomerRequest(token, caseId, request.id, outcome);
      await refresh();
      onCaseChanged();
    } catch (err) {
      setActionError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Failed to update the request.'
      );
    } finally {
      setBusyId(null);
    }
  };

  // Flag off and nothing asked: the case page looks exactly as before.
  if (!canAsk && (requests === null || requests.length === 0)) {
    return null;
  }

  return (
    <div className="documents-section customer-requests">
      <div className="section-header">
        <h2>Requests to the customer</h2>
        {canAsk && (
          <button
            type="button"
            className="financas-btn financas-btn--primary"
            onClick={() => setDialog({ reason: null, source: null })}
          >
            Ask the customer
          </button>
        )}
      </div>

      {loadError && <p className="financas-office-error" role="alert">{loadError}</p>}
      {actionError && <div className="status-error-banner" role="alert">{actionError}</div>}

      {requests && requests.length === 0 && (
        <p className="creq-empty">Nothing asked yet. The customer gets an email and a page to answer on.</p>
      )}

      {requests?.map(request => {
        const pill = STATUS_PILL[request.status] ?? { label: request.status, tone: 'muted' };
        const busy = busyId === request.id;
        const reminders = request.reminder_count ?? 0;
        return (
          <article
            key={request.id}
            className={`creq-card${request.status === 'answered' ? ' creq-card--answered' : ''}`}
            data-testid="customer-request"
          >
            <header className="creq-card-header">
              <span className="creq-reason">{reasonLabel(request.reason)}</span>
              {request.source === 'financas' && <span className="creq-source">Finanças asked</span>}
              <span className={`creq-pill creq-pill--${pill.tone}`}>{pill.label}</span>
            </header>

            <CheckLine request={request} />

            <p className="creq-note">{request.note}</p>

            <p className="creq-meta">
              Asked by {request.created_by_name || request.created_by || 'the lawyer'}
              {toDate(request.created_at) && (
                <>
                  {' · '}
                  <When value={request.created_at} />
                </>
              )}
              {reminders > 0 && (
                <>
                  {' · '}
                  Reminded {reminders === 1 ? 'once' : `${reminders} times`}
                  {toDate(request.reminded_at) && (
                    <>
                      , last <When value={request.reminded_at} />
                    </>
                  )}
                </>
              )}
            </p>

            <ResponseBlock request={request} onViewDocument={onViewDocument} />

            {request.status === 'answered' && (
              <div className="creq-actions">
                <button
                  type="button"
                  className="financas-btn financas-btn--primary"
                  onClick={() => handleClose(request, 'accepted')}
                  disabled={busy}
                >
                  {busy ? 'Saving...' : 'Accept'}
                </button>
                {canAsk && (
                  <button
                    type="button"
                    className="financas-btn"
                    onClick={() => setDialog({ reason: request.reason, source: request.source })}
                    disabled={busy}
                  >
                    Ask again
                  </button>
                )}
              </div>
            )}
            {request.status === 'open' && (
              <div className="creq-actions">
                <button
                  type="button"
                  className="financas-btn financas-btn--link"
                  onClick={() => handleClose(request, 'cancelled')}
                  disabled={busy}
                >
                  {busy ? 'Cancelling...' : 'Cancel request'}
                </button>
              </div>
            )}
          </article>
        );
      })}

      {dialog && (
        <AskCustomerDialog
          caseId={caseId}
          user={user}
          lawyerStatus={lawyerStatus}
          applicantType={applicantType}
          documentType={documentType}
          caseValues={caseValues}
          initialReason={dialog.reason}
          initialSource={dialog.source}
          onCancel={() => setDialog(null)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default CustomerRequests;
